import { Haptics, ImpactStyle } from '@capacitor/haptics'
import { HiDotsHorizontal } from 'react-icons/hi'
import {
    IonButton,
    IonCard,
    IonContent,
    IonIcon,
    IonItem,
    IonItemOption,
    IonItemOptions,
    IonItemSliding,
    IonLabel,
    IonList,
    IonPopover,
    IonRippleEffect,
    useIonAlert,
	useIonActionSheet,
	useIonRouter,
} from '@ionic/react'
import { checkmarkSharp, createOutline, exitOutline, recordingOutline, trashOutline } from 'ionicons/icons'
import React, { useState, useEffect, useRef } from 'react'
import { doc, getDoc, runTransaction, updateDoc } from 'firebase/firestore'

import { deepCopy, getUrl } from './util'
import {
	uniqueTeamNames,
	useCanEditItem,
	useDeleteItem,
	useMyRelevantTeam,
	useNewTeamIdState,
	useSection,
	useSetShowTeamSelection,
	useUser,
} from './data'
import EditItem from './EditItem'
import { useSaveItem } from './functions'
import { pushTeamSeasonRoot } from './navigation'
import { dbids } from './definitions'
import { db } from './firebase'
import { SeasonEditor } from './SeasonEditor'
import GameInsightIcon from './GameInsightIcon'

type ItemProps<T> = {
	item: T
	items: Array<T>
	type: ItemTypeDefinition<T>
	editMode: boolean|string
	setEditMode: (editMode: boolean|string) => void
}

type ItemAction = {
	id: string
	label: string
	slidingLabel: string
	slidingIconOnly?: boolean
	icon?: string | React.ReactElement<{ slot?: string }>
	color?: string
	onSelect: () => void
}

const Item = <T extends Item>(props: ItemProps<T>) => {

	const { item, items, type, editMode } = props

	    const canEdit = useCanEditItem(type?.unit)
	const deleteItem = useDeleteItem<T>()
	    const [ presentAlert, dismissAlert ] = useIonAlert()
	    const [ newTeamId, setNewTeamId ] = useNewTeamIdState()
	const section = useSection()
	const user = useUser()
	const myRelevantTeam = useMyRelevantTeam()
	const router = useIonRouter()
	const slidingItemRef = useRef<HTMLIonItemSlidingElement>(null)

	const toggleEditMode = () => props.setEditMode(!editMode)
	const setShowTeamSelection = useSetShowTeamSelection()
	const saveItem = useSaveItem<T>()

	const gameInsightAction = useGameInsightAction(type.unit === 'game' ? item as unknown as Game : null)

	// console.log('item', item, 'type', type, 'canEdit', canEdit)

    useEffect(() => {
		let timer: ReturnType<typeof setTimeout>

        if (editMode) {
            timer = setTimeout(() => {

                const id = `editable-item-id-${item.id}`

                if (editMode === 'result') {
					const element = document.querySelector(`#${id} .enter-results-table tr td:nth-child(2) input`) as HTMLInputElement | null
					if (element) {
						console.log('Focus!')
						element.focus()
					}
				} else {
					const formElement = document.getElementById(id)
					if (formElement) {
						formElement.scrollIntoView({
							behavior: 'smooth',
							block: 'end', // Use 'block' instead of 'alignToTop'
						})
					}
                }
            }, 120)
        }

        return () => clearTimeout(timer)
    }, [ editMode, item.id ])

	const handleChange = async (data: T) => {

		const newData = deepCopy(data)

		/*
		 * Removed because I think this was just to save some storage, but
		 * it creates illegal 'result: undefined' fields.
		 *
        if ('result' in newData && newData.result && !newData.result.me.filter(score => score !== null).length && !newData.result.them.filter(score => score !== null).length) {
            newData.result = undefined
        }
		*/

		//
		// Write to Firebase
		//
		try {
			// Respond immediately
			toggleEditMode()
			const { unit } = type
			if (!(unit in dbids)) {
				throw new Error("Illegal attempt to save item type " + type.name)
			}
			await saveItem(unit as keyof DbidMapping, newData)
		} catch (err) {
			console.error("Failed to update doc", err)
			// Switch back to edit mode to show it didn't work
			toggleEditMode()
		}
	}

	const handleSeasonPatch = async (patch: Partial<Season> & { id: SeasonId }) => {
		try {
			const teamId = newTeamId || section.team
			if (!teamId) {
				throw new Error('Missing team while updating season')
			}
			const { id, ...data } = patch
			await updateDoc(doc(db, `/teams/${teamId}/seasons`, id), data)
		} catch (err) {
			console.error("Failed to update season", err)
			presentAlert({
				header: 'Unable to Save',
				message: 'Season settings could not be updated. Please try again.',
				buttons: [ 'OK' ],
			})
			throw err
		}
	}

	const presentDeleteError = (err: unknown) => {
		console.error("Failed to delete item", err)
		presentAlert({
			header: 'Unable to Delete',
			message: `This ${type.unit} could not be deleted. Please check your connection and try again.`,
			buttons: [ 'OK' ],
		})
	}
	const viewSeason = () => {
		if (type.unit !== 'season') {
			return
		}

		const teamId = newTeamId || section.team
		if (!teamId || !item.id) {
			console.error('Missing team or season while viewing season', { teamId, seasonId: item.id })
			return
		}

		const url = getUrl({
			relative: true,
			page: 'games',
			section: {
				team: teamId,
				season: item.id,
			},
		})
		setNewTeamId(null)
		setShowTeamSelection(false)
		pushTeamSeasonRoot(router, url)
	}
	const viewTeam = () => {
		if (type.unit === 'team') {
			setNewTeamId(item.id || null)
		}
	}

	const confirmDelete = async (allowDifferentContext = false) => {
		try {
			const alertData = await deleteItem({
				type,
				item,
				onError: presentDeleteError,
				allowDifferentContext,
				onConfirmDifferentContext: async () => {
					await dismissAlert()
					await confirmDelete(true)
					return false
				},
				onViewSeason: async () => {
					await dismissAlert()
					viewSeason()
					return false
				},
				onViewTeam: async () => {
					await dismissAlert()
					viewTeam()
					return false
				},
				parentName: type.unit === 'season' ? myRelevantTeam?.name : undefined,
				ownedByTeamId: type.unit === 'season' ? myRelevantTeam?.id : undefined,
			})
			presentAlert(alertData)
		} catch (err) {
			presentDeleteError(err)
		}
	}

	const onClickDelete = () => {
		const deletionTeam = type.unit === 'team' ? item as unknown as Team : type.unit === 'season' ? myRelevantTeam : null
		const shouldWarnCoCoach = !!user && !!deletionTeam?.managers?.includes(user.uid) && deletionTeam.managers.length > 1

		if (!shouldWarnCoCoach) {
			confirmDelete()
			return
		}

		presentAlert({
			header: 'Co-Coach Warning',
			message: "Removing data deletes it for all of this team's coaches, not just you. Do you want to proceed?",
			buttons: [
				{
					text: 'Cancel',
					role: 'cancel',
				},
				{
					text: 'Continue',
					handler: async () => {
						await dismissAlert()
						await confirmDelete()
						return false
					},
				},
			],
		})
	}

	const confirmStopCoaching = async () => {
		if (!user || type.unit !== 'team') {
			return
		}

		const teamItem = item as unknown as Team
		const teamName = teamItem.name || 'this team'
		const teamRef = doc(db, '/teams', teamItem.id)

		try {
			await runTransaction(db, async transaction => {
				const teamSnapshot = await transaction.get(teamRef)
				const team = teamSnapshot.data() as Team | undefined
				const managers = team?.managers || [ ]

				if (!managers.includes(user.uid)) {
					throw new Error('You are not a coach of this team.')
				}

				if (managers.length <= 1) {
					throw new Error('This team needs at least one coach.')
				}

				transaction.update(teamRef, {
					managers: managers.filter(uid => uid !== user.uid),
				})
			})

			presentAlert({
				header: 'Stopped Coaching',
				message: `You are no longer a coach of ${teamName}.`,
				buttons: [ 'OK' ],
			})
		} catch (err) {
			console.error("Failed to stop coaching team", err)
			presentAlert({
				header: 'Unable to Stop Coaching',
				message: 'AutoRoster could not update this team. Please check your connection and try again.',
				buttons: [ 'OK' ],
			})
		}
	}

	const onClickStopCoaching = () => {
		const teamItem = item as unknown as Team
		const teamName = teamItem.name || 'this team'

		presentAlert({
			header: 'Resign as co-coach',
			message: `Do you really want to stop co-coaching ${teamName}? You will no longer be able to modify it.`,
			buttons: [
				{
					text: 'Cancel',
					role: 'cancel',
				},
				{
					text: 'OK',
					handler: confirmStopCoaching,
				},
			],
		})
	}

	const canStopCoaching = type.unit === 'team'
		&& !!user
		&& (item as unknown as Team).managers?.includes(user.uid)
		&& (item as unknown as Team).managers.length > 1

    const allFields = type.fields.concat(type.editOnlyFields || [ ])
	const uniqueNames = (type.unit === 'team' && allFields.length === 1 && allFields[0] === 'name') ? uniqueTeamNames(items as Array<Team>) : null

	// console.log('Rendering item', type.unit, item.id, 'editMode', editMode, 'allFields', allFields, 'items', items, 'uniqueNames', uniqueNames)

    const myClasses = [ 'autoroster-item', type.unit ]

	let content
	let routerLink
	let onClick: null | (() => void) = null
	const handleItemClick = () => {
		void Haptics.impact({ style: ImpactStyle.Light })
		if (onClick) {
			onClick()
		}
	}
	const handleSlidingItemPointerDown = (event: React.PointerEvent<HTMLIonItemSlidingElement>) => {
		const target = event.target as Element
		if (
			slidingItemRef.current?.classList.contains('item-sliding-active-slide')
			&& !target.closest('ion-item-option')
		) {
			event.preventDefault()
			event.stopPropagation()
			void slidingItemRef.current.close()
		}
	}
	const handleSlidingActionSelect = async (action: ItemAction) => {
		await slidingItemRef.current?.close()
		action.onSelect()
	}
	const actions = getItemActions({
		canEdit,
		unit: type.unit,
		onClickEdit: toggleEditMode,
		onClickDelete,
		onClickStopCoaching: canStopCoaching ? onClickStopCoaching : undefined,
		gameInsightAction,
	})

	    if (editMode) {
			if (type.unit === 'season') {
				return (
					<SeasonEditor
						mode="edit"
						season={item as Season}
						className={`editable-item autoroster-item ${type.unit}`}
						onSavePatch={handleSeasonPatch}
						connectorMode={newTeamId ? 'hidden' : 'current'}
						onClose={toggleEditMode}
					/>
				)
			}
	        const submitButtonContent = (
	            <>
	                <IonIcon icon={checkmarkSharp} /> Update
            </>
        )
        return (
            <EditItem
                mode="edit"
                editMode={editMode}
                type={type}
                item={item}
                items={items}
                className={`editable-item autoroster-item ${type.unit}`}
                submitButtonContent={submitButtonContent}
                onSubmit={handleChange}
                onCancel={toggleEditMode}
            />
        )
    } else {

        if (type.linkToItem) {

            routerLink = `/${type.name}/${item.id}`

		} else if (type.unit === 'season') {
			onClick = viewSeason

		}

		if (type.unit === 'team') {

			onClick = viewTeam
		}

		if (!routerLink && !onClick) {
            console.error("Bad attempt to link", type, item)
        }

        content = (
            <div
                className={myClasses.join(' ')}
            >

                <IonRippleEffect></IonRippleEffect>

					<ItemMenu
						actions={actions}
                />
				<div className="item-content">
                {
                    allFields.map((field, index) => {
						const value = (field === 'name' && uniqueNames?.[item.id] as T[keyof T]) || item[field]
						return (
							<ItemField<T>
								key={String(field) || `item-field-${index}`}
								field={field}
								value={value}
								type={type}
								item={item}
								setEditMode={props.setEditMode}
								/>
						)
					})
                }
				</div>

                {
                    type.ExtraContent && <type.ExtraContent item={item} />
                }
            </div>
        )
    }

	return (
		<IonItemSliding
			ref={slidingItemRef}
			className="autoroster-item-sliding"
			onPointerDown={handleSlidingItemPointerDown}
		>
			<IonItem lines="none" className="autoroster-item-container">
				<IonCard
					button={true}
					routerLink={routerLink}
					onClick={handleItemClick}
				>
					{content}
				</IonCard>
			</IonItem>
			<IonItemOptions side="end">
				{actions.map(action => (
					<IonItemOption
						key={action.id}
						className={`autoroster-item-option autoroster-item-option-${action.id}`}
						color={action.color}
						aria-label={action.slidingLabel}
						onClick={event => {
							event.stopPropagation()
							void handleSlidingActionSelect(action)
						}}
					>
						{action.slidingIconOnly ? (
							renderActionIcon(action, 'icon-only')
						) : (
							<>
								{renderActionIcon(action)}
								<span className={action.icon ? undefined : 'autoroster-item-option-label'}>
									{action.slidingLabel}
								</span>
							</>
						)}
					</IonItemOption>
				))}
			</IonItemOptions>
		</IonItemSliding>
	)
}

type ItemFieldProps<T> = FormatItemFieldProps<T> & {
	type: ItemTypeDefinition<T>
}

const ItemField = <T extends Item>(props: ItemFieldProps<T>) => {
	const { type } = props

	const content = type.formatField ? type.formatField(props) : String(props.value)

	return (
		<div className={`field-${String(props.field)}`}>
			{content}
		</div>
	)
}

const useGameInsightAction = (game: Game | null): ItemAction | undefined => {
	const router = useIonRouter()
	const section = useSection()
	const [ presentActionSheet, dismissActionSheet ] = useIonActionSheet()

	if (!game) {
		return undefined
	}

	const recoverFlowMapFlagIfNeeded = async () => {
		if (game.hasFlowMap || !section.team || !section.season) {
			return false
		}

		const flowRef = doc(db, `/teams/${section.team}/seasons/${section.season}/flows`, game.id)
		const flowSnapshot = await getDoc(flowRef)
		if (!flowSnapshot.exists()) {
			return false
		}

		const flow = flowSnapshot.data() as Partial<FlowData> | undefined
		if (!Array.isArray(flow?.events) || !flow.events.length) {
			return false
		}

		await updateDoc(doc(db, `/teams/${section.team}/seasons/${section.season}/games`, game.id), {
			hasFlowMap: true,
		})
		return true
	}

	const flowMapUrl = getUrl({
		relative: true,
		page: `games/${game.id}/flow-map`,
		section,
	})
	const recordFlowUrl = getUrl({
		relative: true,
		page: `games/${game.id}/record-flows`,
		section,
	})

	const openRecordFlows = () => {
		router.push(recordFlowUrl, 'forward', 'push')
	}

	const presentFlowMapActionSheet = (hasFlowMap = !!game.hasFlowMap) => {
		presentActionSheet({
			header: 'Flow Map',
			buttons: [
				...(hasFlowMap ? [ {
					text: 'Dashboard',
					handler: () => {
						router.push(flowMapUrl, 'forward', 'push')
					},
				} ] : [ ]),
				{
					text: 'Record',
					icon: recordingOutline,
					handler: () => {
						openRecordFlows()
					},
				},
				{
					text: 'Cancel',
					role: 'cancel',
				},
			],
		})
	}

	return {
		id: 'flow-map',
		label: 'Flow Map',
		slidingLabel: 'Flow Map',
        slidingIconOnly: true,
		icon: <GameInsightIcon />,
		onSelect: () => {
			void recoverFlowMapFlagIfNeeded().then(recovered => {
				if (recovered) {
					void dismissActionSheet().then(() => presentFlowMapActionSheet(true))
				}
			}).catch(error => {
				console.warn('Unable to recover Flow Map flag.', error)
			})
			presentFlowMapActionSheet()
		},
	}
}

type ItemMenuProps = {
	actions: ItemAction[]
}

type ItemActionProps = {
	action: ItemAction
	onSelect: () => void
}

const renderActionIcon = (action: ItemAction, slot?: string) => {
	if (!action.icon) {
		return null
	}

	if (typeof action.icon === 'string') {
		return <IonIcon icon={action.icon} slot={slot} aria-hidden="true" />
	}

	return React.cloneElement(action.icon, slot ? { slot } : undefined)
}

const getItemActions = (props: {
	canEdit: boolean
	unit: string
	onClickEdit: () => void
	onClickDelete: () => void
	onClickStopCoaching?: () => void
	gameInsightAction?: ItemAction
}): ItemAction[] => {
	const actions: ItemAction[] = []

	if (props.canEdit) {
		actions.push(
			{
				id: 'edit',
				label: `Edit ${props.unit}`,
				slidingLabel: 'Edit',
				slidingIconOnly: true,
				icon: createOutline,
				onSelect: props.onClickEdit,
			},
			{
				id: 'delete',
				label: `Delete ${props.unit}`,
				slidingLabel: 'Delete',
				slidingIconOnly: true,
				icon: trashOutline,
				color: 'danger',
				onSelect: props.onClickDelete,
			},
		)
	}

	if (props.onClickStopCoaching) {
		actions.push({
			id: 'stop-coaching',
			label: 'Stop Coaching',
			slidingLabel: 'Stop Coaching',
			icon: exitOutline,
			color: 'danger',
			onSelect: props.onClickStopCoaching,
		})
	}

	if (props.gameInsightAction) {
		actions.unshift(props.gameInsightAction)
	}

	return actions
}

const ItemActionButton = (props: ItemActionProps) => (
	<IonButton
		className={`item-menu-item item-menu-${props.action.id}`}
		onClick={event => {
			props.action.onSelect()
			props.onSelect()
			event.stopPropagation()
			event.preventDefault()
		}}
		expand="full"
		fill="clear"
		color={props.action.color}
	>
		{props.action.icon ? renderActionIcon(props.action, 'start') : <IonIcon icon={createOutline} className="item-menu-icon-placeholder" slot="start" aria-hidden="true" />}
		<IonLabel>{props.action.label}</IonLabel>
	</IonButton>
)

const ItemMenu = (props: ItemMenuProps) => {

	const { actions } = props

	const [ showPopover, setShowPopover ] = useState(false)

	const popover = useRef<HTMLIonPopoverElement>(null)

	if (!actions.length) {
		return null
	}

	const handleClick = (event: React.MouseEvent) => {
		event.stopPropagation()
		event.preventDefault()
		if (popover.current) {
			popover.current.event = event
			setShowPopover(true)
		}
	}

	const handleClose = (event: React.MouseEvent) => {
		event.stopPropagation()
		event.preventDefault()
        setShowPopover(false)
	}

	const closePopover = () => setShowPopover(false)

	return (
		<div className="item-menu">
            <IonButton
                className="button-item-menu button-react-icon"
                fill="clear"
                color="medium"
                onClick={handleClick}
            >
                <HiDotsHorizontal />
            </IonButton>
            <IonPopover
                ref={popover}
                isOpen={showPopover}
                cssClass="item-menu-overlay"
                reference="event"
                dismissOnSelect={true}

				// @ts-ignore
                onDidDismiss={handleClose}
            >
                <IonContent>
					<IonList>
						{actions.map(action => (
							<ItemActionButton
								key={action.id}
								action={action}
								onSelect={closePopover}
							/>
						))}
					</IonList>
				</IonContent>
			</IonPopover>
		</div>
	)
}

export default Item
